// src/components/pages/PrivacyPolicy.js
import React, { useEffect } from 'react';
import SeoHead from '../seo/SeoHead';
import { Box, Container, Typography, useTheme } from '@mui/material';

export default function PrivacyPolicy() {
  const theme = useTheme();

  // scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SeoHead
        title="Privacy Policy – Marrakech Reserve"
        description="Read how Marrakech Reserve collects, uses, and protects your personal data when you visit our site or make a purchase."
        url="https://marrakech-reserve.com/privacy-policy"
        keywords="privacy policy, data protection, Marrakech Reserve, olive oil, personal data, GDPR, cookies"
      />

      <Box sx={{ bgcolor: theme.palette.background.default, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            At Marrakech Reserve, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, share, and safeguard your information when you visit our website or purchase our products.
          </Typography>

          <Typography variant="h6" gutterBottom>
            1. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We may collect:
            <ul>
              <li><strong>Contact information:</strong> name, email address, shipping address, phone number.</li>
              <li><strong>Payment details:</strong> billing address, credit/debit card information (processed securely via Stripe).</li>
              <li><strong>Usage data:</strong> pages visited, time spent, referral sources, cookie data.</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            Your data helps us:
            <ul>
              <li>Process and fulfill orders</li>
              <li>Provide customer support</li>
              <li>Send order confirmations, shipping updates, and marketing communications (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom>
            3. Cookies & Tracking
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies and similar technologies to:
            <ul>
              <li>Remember your preferences</li>
              <li>Analyze site traffic and performance</li>
              <li>Serve personalized content and ads</li>
            </ul>
            You can manage cookie settings in your browser or through our cookie consent banner.
          </Typography>

          <Typography variant="h6" gutterBottom>
            4. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement industry‑standard security measures (encryption, secure servers) to protect your information. However, no online system is completely infallible.
          </Typography>

          <Typography variant="h6" gutterBottom>
            5. Third‑Party Services
          </Typography>
          <Typography variant="body1" paragraph>
            We may share data with:
            <ul>
              <li><strong>Stripe:</strong> for payment processing.</li>
              <li><strong>Mailing providers:</strong> for email notifications.</li>
              <li><strong>Analytics platforms:</strong> for website performance insights.</li>
            </ul>
            Each third party follows its own privacy policy.
          </Typography>

          <Typography variant="h6" gutterBottom>
            6. Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have rights to:
            <ul>
              <li>Access, correct, or delete your personal data</li>
              <li>Object to or restrict processing</li>
              <li>Receive a copy of your data in a portable format</li>
            </ul>
            To exercise these rights, please contact us at <a href="mailto:support@marrakech-reserve.com">support@marrakech-reserve.com</a>.
          </Typography>

          <Typography variant="h6" gutterBottom>
            7. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy periodically. We’ll post the revised date at the top and notify you of significant changes.
          </Typography>

          <Typography variant="h6" gutterBottom>
            8. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions or concerns about this Privacy Policy, please email us at <a href="mailto:support@marrakech-reserve.com">support@marrakech-reserve.com</a> or write to:
          </Typography>
          <Typography variant="body2" paragraph>
            Marrakech Reserve<br/>
            17 Rue Yougouslavia<br/>
            Marrakech, Morocco
          </Typography>
        </Container>
      </Box>
    </>
  );
}
